import React, { useState, useEffect } from 'react';

interface SessionWarningModalProps {
  open: boolean;
  onContinue: () => void;
  onLogout: () => void;
  language: 'en' | 'es';
}

const SessionWarningModal: React.FC<SessionWarningModalProps> = ({
  open,
  onContinue,
  onLogout,
  language
}) => {
  const [countdown, setCountdown] = useState(60);

  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;
    let isLoggingOut = false;

    if (!open) {
      setCountdown(60);
      if (timer) {
        clearInterval(timer);
      }
      isLoggingOut = false;
      return;
    }

    setCountdown(60);
    timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1 && !isLoggingOut) {
          isLoggingOut = true;
          if (timer) clearInterval(timer);
          setTimeout(() => {
            onLogout();
          }, 100);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (timer) {
        clearInterval(timer);
      }
      setCountdown(60);
      isLoggingOut = false;
    };
  }, [open, onLogout]);

  useEffect(() => {
    const handleForceClose = () => {
      setCountdown(60);
      onLogout();
    };

    window.addEventListener('forceCloseSessionWarning', handleForceClose);
    
    return () => {
      window.removeEventListener('forceCloseSessionWarning', handleForceClose);
    };
  }, [onLogout]);

  if (!open || !localStorage.getItem('token')) return null;

  const t = {
    en: {
      title: 'Session Expiring Soon',
      message: 'Your session will expire due to inactivity in:',
      continue: 'Stay Logged In',
      logout: 'Log Out Now'
    },
    es: {
      title: 'Sesión Por Expirar',
      message: 'Tu sesión expirará por inactividad en:',
      continue: 'Mantener Sesión',
      logout: 'Cerrar Sesión'
    }
  };

  return (
    <>
      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        .animate-fade-in { animation: fadeIn 0.3s ease; }
        .animate-slide-up { animation: slideUp 0.3s ease; }
      `}</style>
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[10000] animate-fade-in">
      <div className="bg-[var(--card-bg)] p-8 rounded-2xl max-w-[400px] w-[90%] text-center shadow-2xl animate-slide-up">
        <div className="text-7xl mb-4 flex items-center justify-center h-16 leading-none">
          <span>⏰</span>
        </div>
        <h2 className="text-xl font-bold text-white mb-2">{t[language].title}</h2>
        <p className="text-muted mb-0">{t[language].message}</p>
        <div className="text-5xl font-bold text-primary my-6">{countdown}s</div>
        <div className="flex gap-4 mt-8">
          <button
            onClick={() => {
              setCountdown(60);
              onContinue();
            }}
            className="flex-1 py-3 bg-gradient-to-r from-primary to-primary-dark text-white border-none rounded-lg font-semibold cursor-pointer"
          >
            {t[language].continue}
          </button>
          <button
            onClick={() => {
              setCountdown(0);
              onLogout();
            }}
            className="flex-1 py-3 bg-transparent text-white border-2 border-[var(--border-color)] rounded-lg font-semibold cursor-pointer hover:bg-white/5"
          >
            {t[language].logout}
          </button>
        </div>
      </div>
    </div>
    </>
  );
};

export default SessionWarningModal;